import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

const initialState = {
  items: [],
  filteredItems: [],
  loading: false,
  error: null,
  hasMore: true,
  currentPage: 1,
  searchTerm: '',
  totalCount: 0,
  searchResults: [],
  isSearching: false,
  searchError: null
};

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    setSearchLoading: (state, action) => {
      state.isSearching = action.payload;
    },
    setSearchResults: (state, action) => {
      state.searchResults = action.payload.products;
      state.totalCount = action.payload.products_count;
      state.hasMore = action.payload.products_count > state.searchResults.length;
    },
    setSearchError: (state, action) => {
      state.searchError = action.payload;
      state.isSearching = false;
    },
    clearSearch: (state) => {
      state.searchResults = [];
      state.searchTerm = '';
      state.isSearching = false;
      state.searchError = null;
    },
    setProducts: (state, action) => {
      // اضافه کردن محصولات جدید به آرایه موجود
      const newProducts = action.payload.filter(
        newProduct => !state.items.some(
          existingProduct => existingProduct.id === newProduct.id
        )
      );
      state.items = [...state.items, ...newProducts];
      state.filteredItems = state.items; // اگر سرچی نباشه، همه محصولات مایش داده میشن
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setLoadingMore: (state, action) => {
      state.loadingMore = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setHasMore: (state, action) => {
      state.hasMore = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setInitialized: (state, action) => {
      state.initialized = action.payload;
    },
    filterProducts: (state, action) => {
      const searchTerm = action.payload.toLowerCase();
      state.searchTerm = searchTerm;
      
      if (!searchTerm) {
        state.filteredItems = state.items;
        return;
      }

      state.filteredItems = state.items.filter(product => {
        const title = (product.title || '').toLowerCase();
        const vendor = (product.vendor?.title || '').toLowerCase();
        
        return (
          title.includes(searchTerm) || 
          vendor.includes(searchTerm)
        );
      }).sort((a, b) => {
        // اولویت با محصولاتی که با عبارت جستجو شروع می‌شوند
        const aTitle = (a.title || '').toLowerCase();
        const bTitle = (b.title || '').toLowerCase();
        const aStartsWith = aTitle.startsWith(searchTerm);
        const bStartsWith = bTitle.startsWith(searchTerm);
        
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;
        return 0;
      });
    },

    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    }
  }
});

export const { 
  setSearchLoading, 
  setSearchResults, 
  setSearchError, 
  clearSearch,
  setProducts,
  setLoading,
  setLoadingMore,
  setError,
  setHasMore,
  setCurrentPage,
  setInitialized,
  filterProducts,
  setSearchTerm
} = productsSlice.actions;

// اکشن برای جستجوی محصولات
export const searchProducts = (searchParams) => async (dispatch, getState) => {
  try {
    dispatch(setSearchLoading(true));
    
    const token = localStorage.getItem('token');
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/basalam/products/search`,
      {
        params: {
          product_name: searchParams.product_name,
          page: searchParams.page,
          page_size: searchParams.page_size
        },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      }
    );

    if (response.data && response.data.products) {
      const transformedData = {
        products: response.data.products.map(product => ({
          ...product,
          id: product.id,
          title: product.title,
          price: product.price,
          inventory: product.inventory,
          status: product.status,
          photo: {
            small: product.photo?.small || product.photo?.extra_small,
            medium: product.photo?.medium || product.photo?.small,
            large: product.photo?.large || product.photo?.medium
          }
        })),
        products_count: response.data.products_count || 0,
        result_count: response.data.result_count || 0,
        pagination: response.data.pagination || {
          current_page: searchParams.page,
          per_page: searchParams.page_size,
          total_pages: Math.ceil((response.data.products_count || 0) / searchParams.page_size)
        }
      };

      dispatch(setSearchResults(transformedData));
      dispatch(setSearchTerm(searchParams.product_name));
    } else {
      dispatch(setSearchError('داده‌های نامعتبر از سرور دریافت شد'));
    }
  } catch (error) {
    console.error('Search error:', error);
    let errorMessage = 'خطا در جستجوی محصولات';
    
    if (error.response?.data?.detail) {
      errorMessage = typeof error.response.data.detail === 'object' 
        ? JSON.stringify(error.response.data.detail) 
        : error.response.data.detail;
    }
    
    dispatch(setSearchError(errorMessage));
  } finally {
    dispatch(setSearchLoading(false));
  }
};

export default productsSlice.reducer;
